// src/features/api/staffApi.js
// @flow

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import SPBackend from '../../services/SPBackend';
import { log } from '../../utils/jsUtils';
import { updateRequests as updateFeedRequests } from '../../reduxStore/modules/feed';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { auth } from '../../services/firebase';

export const staffApi = createApi({
  reducerPath: 'staffApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  tagTypes: ['StaffList'],
  endpoints: (builder) => ({
    // Assign staff to tasks
    assignStaffToTasks: builder.mutation({
      queryFn: async ({ taskIds, assigneeId }) => {
        try {
          const firebaseUser = auth.currentUser;
          if (!firebaseUser) {
            return { error: { status: 'AUTH_ERROR', data: 'Not authenticated' } };
          }

          log('[staffApi] Assigning staff to tasks:', { taskIds, assigneeId });
          const response = await SPBackend.assignStaffToTasks(taskIds, assigneeId, firebaseUser);

          return {
            data: {
              ...response.data,
              taskIds,
              assigneeId
            }
          };
        } catch (error) {
          log('[staffApi] Error assigning staff:', error);
          return {
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to assign staff'
            }
          };
        }
      },
      // Optimistic updates
      onQueryStarted: async (
        { taskIds, assignee, existingRequests, showNotification = true },
        { dispatch, queryFulfilled }
      ) => {
        log('[staffApi.assignStaffToTasks] Starting optimistic update', {
          taskIds,
          assignee,
          requestsCount: existingRequests?.length || 0
        });

        // Store original requests for rollback
        const originalRequests = existingRequests ? [...existingRequests] : [];

        // Apply optimistic update to feed
        if (existingRequests && existingRequests.length > 0) {
          const updatedRequests = existingRequests.map(req => {
            if (taskIds.includes(req._id)) {
              return {
                ...req,
                Assignee: assignee
              };
            }
            return req;
          });

          dispatch(updateFeedRequests(updatedRequests));
        }

        try {
          await queryFulfilled;

          // Show success notification
          if (showNotification) {
            dispatch(setSnackMessage({
              message: taskIds.length > 1
                ? `${taskIds.length} tasks assigned.`
                : 'Task assigned.',
              level: 'success',
              duration: 3000
            }));
          }
        } catch (err) {
          log('[staffApi.assignStaffToTasks] Error, rolling back:', err);

          // Rollback on failure
          if (originalRequests.length > 0) {
            dispatch(updateFeedRequests(originalRequests));
          }

          dispatch(setSnackMessage({
            message: 'Failed to assign task. Please try again.',
            level: 'error',
            duration: 5000
          }));
        }
      }
    }),

    // Get staff list
    getStaffList: builder.query({
      queryFn: async () => {
        try {
          const firebaseUser = auth.currentUser;
          if (!firebaseUser) {
            return { error: { status: 'AUTH_ERROR', data: 'Not authenticated' } };
          }

          log('[staffApi] Fetching staff list');
          const response = await SPBackend.getUsers(firebaseUser);

          return { data: response.data };
        } catch (error) {
          log('[staffApi] Error fetching staff list:', error);
          return {
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to fetch staff'
            }
          };
        }
      },
      providesTags: ['StaffList']
    }),

    // Create new staff member
    createStaff: builder.mutation({
      queryFn: async ({ payload }) => {
        try {
          const firebaseUser = auth.currentUser;
          if (!firebaseUser) {
            return { error: { status: 'AUTH_ERROR', data: 'Not authenticated' } };
          }

          log('[staffApi] Creating staff:', payload);
          const response = await SPBackend.createStaff(payload, firebaseUser);

          return { data: response.data };
        } catch (error) {
          log('[staffApi] Error creating staff:', error);
          return {
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to create staff'
            }
          };
        }
      },
      invalidatesTags: ['StaffList']
    }),

    // Remove staff member
    removeStaff: builder.mutation({
      queryFn: async ({ staff }) => {
        try {
          const firebaseUser = auth.currentUser;
          if (!firebaseUser) {
            return { error: { status: 'AUTH_ERROR', data: 'Not authenticated' } };
          }

          log('[staffApi] Removing staff:', staff._id);
          const response = await SPBackend.removeStaff(staff, firebaseUser);

          return { data: response.data };
        } catch (error) {
          log('[staffApi] Error removing staff:', error);
          return {
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to remove staff'
            }
          };
        }
      },
      invalidatesTags: ['StaffList']
    }),

    // Edit staff member
    editStaff: builder.mutation({
      queryFn: async ({ staff, changedStaff }) => {
        try {
          const firebaseUser = auth.currentUser;
          if (!firebaseUser) {
            return { error: { status: 'AUTH_ERROR', data: 'Not authenticated' } };
          }

          log('[staffApi] Editing staff:', staff._id, changedStaff);
          const response = await SPBackend.editStaff(staff, changedStaff, firebaseUser);

          return { data: response.data };
        } catch (error) {
          log('[staffApi] Error editing staff:', error);
          return {
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to edit staff'
            }
          };
        }
      },
      invalidatesTags: ['StaffList']
    }),

    // Update staff call forward setting
    updateStaffCallForward: builder.mutation({
      queryFn: async ({ staffUser, newValue }) => {
        try {
          const firebaseUser = auth.currentUser;
          if (!firebaseUser) {
            return { error: { status: 'AUTH_ERROR', data: 'Not authenticated' } };
          }

          log('[staffApi] Updating staff call forward:', { staffId: staffUser._id, newValue });
          const response = await SPBackend.editStaff(
            staffUser,
            { ReceiveInboundCalls: newValue },
            firebaseUser
          );

          return { data: response.data };
        } catch (error) {
          log('[staffApi] Error updating staff call forward:', error);
          return {
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to update staff'
            }
          };
        }
      },
      // Optimistic updates with rollback
      onQueryStarted: async (
        { staffUser, newValue },
        { dispatch, queryFulfilled }
      ) => {
        log('[staffApi.updateStaffCallForward] Starting optimistic update', {
          staffId: staffUser._id,
          newValue
        });

        // Optimistic update - patch the cache
        const patchResult = dispatch(
          staffApi.util.updateQueryData('getStaffList', undefined, (draft) => {
            const staffIndex = draft.findIndex(s => s.user._id === staffUser._id);
            if (staffIndex !== -1) {
              draft[staffIndex].user.Settings = {
                ...draft[staffIndex].user.Settings,
                ReceiveInboundCalls: newValue
              };
            }
          })
        );

        try {
          await queryFulfilled;

          dispatch(setSnackMessage({
            message: newValue ? 'Call forward enabled for staff' : 'Call forward disabled for staff',
            level: 'success',
            duration: 3000
          }));
        } catch (err) {
          log('[staffApi.updateStaffCallForward] Error, rolling back:', err);

          // Rollback on failure
          patchResult.undo();

          dispatch(setSnackMessage({
            message: 'Failed to update. Please try again.',
            level: 'error',
            duration: 5000
          }));
        }
      },
      invalidatesTags: ['StaffList']
    })
  })
});

export const {
  useAssignStaffToTasksMutation,
  useGetStaffListQuery,
  useCreateStaffMutation,
  useRemoveStaffMutation,
  useEditStaffMutation,
  useUpdateStaffCallForwardMutation
} = staffApi;
